import React, { useMemo, useState } from "react";
import { Input, Checkbox, Tag } from "antd";

import "./style.scoped.scss";

// "Smith et al., 2023" — enough to tell two same-titled entries apart without
// turning the row into a citation.
const byline = (article) => {
    const first = (article.authors || [])[0];
    const name = [first?.lastName, first?.firstName].filter(Boolean).join(", ");
    const parts = [];
    if (name) {
        parts.push((article.authors || []).length > 1 ? `${name} et al.` : name);
    }
    if (article.publicationYear) {
        parts.push(article.publicationYear);
    }
    const journal = article.abbrJournalName || article.journalName;
    if (journal) {
        parts.push(journal);
    }
    return parts.join(" · ");
};

/**
 * Checkbox picker over the project's L2-included articles of one channel. One
 * instance per CER section, so both channels are fetched once by the modal and
 * passed in rather than loaded here — same arrangement as `SourceDocsPicker`.
 *
 * Selection order is preserved: for the literature section the selection order
 * *is* the §6.2.1 article numbering, so a newly-ticked article is appended
 * rather than sorted into place.
 *
 * An article with no PDF cannot be sent to the CER service at all, so it is
 * listed but not selectable — hiding it would just make the picker look like it
 * had lost an article the user knows they included.
 */
const ArticlePicker = ({ articles = [], value = [], onChange }) => {
    const [filter, setFilter] = useState("");

    // By C-number, which is how the articles are numbered everywhere else in the
    // app — and, for literature, the order the run's citations start out in.
    const sorted = useMemo(() => [...articles].sort((a, b) => (a.mdrId || 0) - (b.mdrId || 0)), [
        articles,
    ]);

    const selectedSet = useMemo(() => new Set(value || []), [value]);

    const filtered = useMemo(() => {
        const tokens = filter
            .split(/\s+/g)
            .filter(Boolean)
            .map((t) => t.toLowerCase());
        if (tokens.length === 0) {
            return sorted;
        }
        return sorted.filter((a) => {
            const haystack = [a.cid, a.title, byline(a)].filter(Boolean).join(" ").toLowerCase();
            return tokens.every((t) => haystack.includes(t));
        });
    }, [sorted, filter]);

    // Everything below counts only the selectable rows: an article with no file
    // must not make "select all" look partially applied forever.
    const selectable = useMemo(() => filtered.filter((a) => a.hasFile), [filtered]);
    const missingFiles = useMemo(() => sorted.filter((a) => !a.hasFile).length, [sorted]);

    const selectAllState = useMemo(() => {
        if (selectable.length === 0) {
            return { checked: false, indeterminate: false };
        }
        const count = selectable.filter((a) => selectedSet.has(a._id)).length;
        if (count === 0) {
            return { checked: false, indeterminate: false };
        }
        if (count === selectable.length) {
            return { checked: true, indeterminate: false };
        }
        return { checked: false, indeterminate: true };
    }, [selectable, selectedSet]);

    const emit = (next) => {
        onChange && onChange(next);
    };

    const toggle = (article) => {
        if (!article.hasFile) {
            return;
        }
        emit(
            selectedSet.has(article._id)
                ? value.filter((v) => v !== article._id)
                : [...value, article._id],
        );
    };

    const toggleAll = () => {
        if (selectAllState.checked) {
            const removing = new Set(selectable.map((a) => a._id));
            emit(value.filter((id) => !removing.has(id)));
            return;
        }
        const added = selectable.map((a) => a._id).filter((id) => !selectedSet.has(id));
        emit([...value, ...added]);
    };

    return (
        <div className="cer-docs-picker cer-article-picker">
            {articles.length === 0 ? (
                <div className="empty">No L2-included articles in this project.</div>
            ) : (
                <>
                    <Input.Search
                        placeholder="Search by C-number, title or author"
                        onSearch={(v) => setFilter(v)}
                        allowClear
                    />
                    <div className="select-all" onClick={toggleAll}>
                        <Checkbox
                            checked={selectAllState.checked}
                            indeterminate={selectAllState.indeterminate}
                            disabled={selectable.length === 0}
                            onClick={(e) => e.stopPropagation()}
                            onChange={toggleAll}
                        />
                        <span className="label">
                            Select all ({selectable.length} article
                            {selectable.length !== 1 ? "s" : ""})
                        </span>
                    </div>
                    <div className="files">
                        {filtered.map((a) => (
                            <div
                                className={`document article${a.hasFile ? "" : " disabled"}`}
                                key={a._id}
                                onClick={() => toggle(a)}
                            >
                                <Checkbox
                                    checked={selectedSet.has(a._id)}
                                    disabled={!a.hasFile}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggle(a)}
                                />
                                <span className="name">
                                    <span className="title">
                                        <span className="cid">{a.cid}</span>
                                        {a.title}
                                    </span>
                                    <span className="byline">{byline(a)}</span>
                                </span>
                                {!a.hasFile && <Tag color="red">Missing File</Tag>}
                            </div>
                        ))}
                    </div>
                    <div className="footer">
                        {selectedSet.size} article{selectedSet.size !== 1 ? "s" : ""} selected
                        {missingFiles > 0 &&
                            ` — ${missingFiles} cannot be selected because no PDF has been retrieved for them`}
                    </div>
                </>
            )}
        </div>
    );
};

export default ArticlePicker;
